import React, { useMemo } from "react";

import type { RowData } from "@tanstack/react-table-v9";

import { gridSpacing } from "@utils";

import Actions from "../../Actions/Actions";
import AdvancedFilters from "../../AdvancedFilters/AdvancedFilters";
import type { AdvancedFiltersProps } from "../../AdvancedFilters/AdvancedFilters";
import type { SavedViewLayout } from "../../AdvancedFilters/SavedFiltersModal/FilterModalProps";
import Button from "../../Button/Button";
import type { ButtonType } from "../../Button/Button";
import DropDown from "../../DropDown/DropDown";
import SearchText from "../../SearchText/SearchText";
import type { TableV2Instance } from "../hooks/useTableV2";
import { ExportBar, TableTitle } from "../styles";
import {
  applyLayout,
  describeLayout,
  parseLayout,
  snapshotLayout,
} from "../tableLayout";
import type { TableV2SelectionMenuItem } from "../types";

export interface TableV2PrimaryButton {
  text?: string;
  icon?: string;
  variant?: ButtonType;
  disabled?: boolean;
  onClick: () => void;
}

export interface TableV2ToolbarProps<TData extends RowData> {
  table: TableV2Instance<TData>;
  isLoading?: boolean;
  headerText?: string;
  primaryButton?: TableV2PrimaryButton;
  /** Extra controls next to the primary button */
  actions?: React.ReactNode;
  /** Shows the search box wired to the table's global filter */
  search?: boolean;
  advancedFilter?: AdvancedFiltersProps;
  selectionMenuItems?: TableV2SelectionMenuItem<TData>[];
  /** Only when the toolbar is used on its own; `<TableV2 />` shows export in the header corner */
  exportAction?: () => void;
  inlineActions?: boolean;
}

const SelectionMenu = <TData extends RowData>({
  table,
  items,
}: {
  table: TableV2Instance<TData>;
  items: TableV2SelectionMenuItem<TData>[];
}) => {
  const selected =
    table.getSelectedRows?.() ??
    table.getSelectedRowModel().rows.map((row) => row.original);
  const count = selected.length;
  const visible = items.filter(
    (item) => !item.condition || item.condition(selected),
  );

  return (
    <DropDown direction="down">
      <DropDown.Toggle disabled={count === 0}>
        <Button
          mb={3}
          text={`${count} selected`}
          icon="fa-solid fa-chevron-down"
          iconPos="right"
          iconStyle={{ marginLeft: gridSpacing[1] }}
          disabled={count === 0}
          variant="secondary"
        />
      </DropDown.Toggle>
      <DropDown.Menu>
        {visible.map((item) => (
          <DropDown.Item
            key={item.title}
            value={item.title}
            onClick={() => item.onClick(selected)}
          >
            {item.title}
          </DropDown.Item>
        ))}
      </DropDown.Menu>
    </DropDown>
  );
};

const TableV2Toolbar = <TData extends RowData>({
  table,
  isLoading = false,
  headerText,
  primaryButton,
  actions,
  search = false,
  advancedFilter,
  selectionMenuItems,
  exportAction,
  inlineActions,
}: TableV2ToolbarProps<TData>) => {
  const globalFilter = table.state.globalFilter;
  const showActions = primaryButton || actions || search || advancedFilter;

  const layoutKey = advancedFilter
    ? JSON.stringify(snapshotLayout(table))
    : undefined;
  const tableLayout = useMemo<SavedViewLayout>(() => {
    return {
      snapshot: () => snapshotLayout(table),
      apply: (layout, slug) => {
        const parsed = parseLayout(layout);
        if (!parsed) return;
        applyLayout(table, parsed);
        table.setAppliedView?.(slug);
      },
      describe: (layout) => {
        const parsed = parseLayout(layout);
        return parsed ? describeLayout(table, parsed) : [];
      },
      restoredView: table.restoredView,
      layoutKey,
    };
  }, [table, table.restoredView, layoutKey]);

  return (
    <>
      {headerText && <TableTitle>{headerText}</TableTitle>}
      {showActions && (
        <Actions inlineActions={inlineActions}>
          {primaryButton && (
            <Button
              variant={primaryButton.variant ?? "primary"}
              icon={primaryButton.icon}
              text={primaryButton.text}
              disabled={primaryButton.disabled}
              onClick={primaryButton.onClick}
            />
          )}
          {actions}
          <Actions.Filters>
            {search && (
              <SearchText
                value={typeof globalFilter === "string" ? globalFilter : ""}
                onChange={(event) => table.setGlobalFilter(event.target.value)}
              />
            )}
            {advancedFilter && (
              <AdvancedFilters
                {...advancedFilter}
                tableLoading={isLoading}
                tableLayout={tableLayout}
              />
            )}
          </Actions.Filters>
        </Actions>
      )}
      {exportAction && !isLoading && (
        <ExportBar>
          <div>
            <Button
              small
              tooltip="Export"
              icon="fa-download"
              variant="link"
              onClick={exportAction}
            />
          </div>
        </ExportBar>
      )}
      {selectionMenuItems && selectionMenuItems.length > 0 && (
        <SelectionMenu table={table} items={selectionMenuItems} />
      )}
    </>
  );
};

export default TableV2Toolbar;
